import { Button, Card, CardHeader, Dialog, DialogActions, DialogSurface, Tab, TabList, TabValue, Title1, Title2, Title3, Spinner, DialogBody, DialogTitle } from "@fluentui/react-components";
import { FC, useEffect, useState } from "react";
import TenderingInformation from "./Tabs//TenderingInformation";
import TenderingTenderingPart from "./Tabs//TenderingTenderingPart";
import TenderingMappings from "./Tabs//TenderingMappings";
import { Tender, Tendering } from "../../models/tendering";
import { getTenderDocumentsByTenderingID, getTenderingDocument, updateTenderingDocument } from "../../services/tenderingService";
import TenderingAdminFilters from "./Tabs//TenderingAdminFilters";
import TenderingIndexing from "./Tabs//TenderingIndexing";
import TenderingRanking from "./Tabs/TenderingRanking";
import TenderingContract from "./Tabs/TenderingContract";
import { useAppContext } from "../../AppContext";
import TenderingAdminContractFee from "./Tabs/TenderingAdminContractFee";
import TenderingVisibility from "./Tabs/TenderingVisibility";
import authService from "../../services/authService";
import { DismissRegular } from "@fluentui/react-icons";

type Props = {
  onClose: () => void;
  open: boolean;
  tenders: Tender[];
  tenderingDocument: Tendering;
  onSave?: () => void;
};

const TenderingSettingsModal: FC<Props> = (props: Props) => {
  const [actualTab, setActualTab] = useState<TabValue>('info');
  const [tenderingDocument, setTenderingDocument] = useState<Tendering>(props.tenderingDocument);
  const [isSaving, setIsSaving] = useState(false);
  
  const {getFeaturePermissionsByModuleID, setUser} = useAppContext();
  const featurePermissions = getFeaturePermissionsByModuleID(props.tenderingDocument.moduleID);

  // Re-sync on open so edits discarded with "Avbryt" don't linger into the next save
  useEffect(() => {
    if (props.open) {
      setTenderingDocument(props.tenderingDocument);
    }
  }, [props.open]);
  
  const onTabChange = (tabValue: TabValue): void => {
    setActualTab(tabValue);
  }

  const saveChanges = async (): Promise<void> => {
    setIsSaving(true);
    try {
      await updateTenderingDocument(tenderingDocument.tenderingID, tenderingDocument);
      const res = await authService.refresh();
      setUser(res.data);

      // Refresh the tendering document on the base page
      if (props.onSave) {
        await props.onSave();
      }
      // Close dialog after successful save
      props.onClose();
    } catch (error) {
      console.error("Error updating tendering document:", error);
      // Don't close dialog on error so user can retry
    } finally {
      setIsSaving(false);
    }
  }

  return(
    <Dialog
      open={props.open}
      onOpenChange={(_event, data) => !data.open && !isSaving && props.onClose()}
    >
      <DialogSurface style={{ maxWidth: '80vw', width: '80vw', height: '90vh', overflow: 'hidden', padding: 0 }}>
        <DialogBody style={{ display: 'flex', flexDirection: 'column',  height: '100%', overflow: 'hidden', padding: '24px' }}>
        <DialogTitle
          action={
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              shape="circular"
              onClick={props.onClose}
              style={{ position: 'absolute', top: '20px', right: '20px' }}
            />
          }
          >Upphandlingsinställningar {tenderingDocument?.tenderingName}
        </DialogTitle>
        <div className="flex flex-row mt-5 p-1 gap-5" style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden' }}>
          <TabList 
            selectedValue={actualTab}
            onTabSelect={(_, data) => onTabChange(data.value as TabValue)}
            className="flex-none"
            vertical
          >
            {featurePermissions?.some(fp => fp.action == 'Tendering settings/General settings') && <Tab value="info" disabled={isSaving}>Information</Tab>}
            {<Tab value="tenderingPart" disabled={isSaving}>Delområden</Tab>}
            {featurePermissions?.some(fp => fp.action == 'Tendering settings/Mappings') && <Tab value="mappings" disabled={isSaving}>Kopplingar</Tab>}
            {featurePermissions?.some(fp => fp.action == 'Tendering settings/Filters') && <Tab value="filters" disabled={isSaving}>Sökfilter</Tab>}
            {featurePermissions?.some(fp => fp.action == 'Tendering settings/Indexing') && <Tab value="indexing" disabled={isSaving}>Indexeringar</Tab>}
            {featurePermissions?.some(fp => fp.action == 'Tendering settings/Ranking') && <Tab value="rankings" disabled={isSaving}>Rangordning & Priser</Tab>}
            {<Tab value="contract-fee" disabled={isSaving}>Inbetalningar</Tab>}
            {<Tab value="digialContract" disabled={isSaving}>Avtal</Tab>}
            {featurePermissions?.some(fp => fp.action == 'Tendering settings/General settings') && <Tab value="visibility" disabled={isSaving}>Synlighet</Tab>}
          </TabList>
            <div className="flex-1 min-h-0 p-1 overflow-y-auto">
            {actualTab === 'info' && <TenderingInformation tenderingDocument={tenderingDocument} updateTenderingData={setTenderingDocument} />}
            {actualTab === 'tenderingPart' && <TenderingTenderingPart tenderingDocument={tenderingDocument} updateTenderingData={setTenderingDocument} />}
            {actualTab === 'mappings' && <TenderingMappings tenderingDocument={tenderingDocument} updateTenderingData={setTenderingDocument} />}
            {actualTab === 'filters' && <TenderingAdminFilters tendering={tenderingDocument} onTenderingChange={setTenderingDocument} />}
            {actualTab === 'indexing' && <TenderingIndexing tenders={props.tenders} tenderingDocument={tenderingDocument} updateTenderingData={setTenderingDocument} />}
            {actualTab === 'rankings' && <TenderingRanking tenders={props.tenders} tenderingDocument={tenderingDocument} updateTenderingData={setTenderingDocument} />}
            {actualTab === 'contract-fee' && <TenderingAdminContractFee tendering={tenderingDocument} onTenderingChange={setTenderingDocument} />}
            {actualTab === 'digialContract' && <TenderingContract tenderingDocument={tenderingDocument} updateTenderingData={setTenderingDocument} />}
            {actualTab === 'visibility' && <TenderingVisibility tenderingDocument={tenderingDocument} updateTenderingData={setTenderingDocument} />}
          </div>
        </div>
        <div>
          <DialogActions>
            <Button 
              onClick={props.onClose}
              disabled={isSaving}
              >
              Avbryt
            </Button>
            <Button 
              appearance="primary" 
              onClick={saveChanges}
              disabled={isSaving}
              icon={isSaving ? <Spinner size="tiny" /> : undefined}
              >
              {isSaving ? "Sparar..." : "Spara"}
            </Button>
          </DialogActions>
        </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}

export default TenderingSettingsModal;